import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../auth/auth.service';

/**
 * Self-service account page (/my-account).
 *
 * Backed by PATCH /auth/me, which resolves the target user from the JWT, so
 * this screen can never edit another account. Username is displayed read-only
 * because it is the login identity; roles, store access and the active flag
 * stay with the admin Users screen.
 *
 * Profile details and credentials are saved by two separate buttons: changing
 * a password should not require re-submitting contact details, and the
 * backend demands the current password only for the credential half.
 */
@Component({
  selector: 'app-my-account',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './my-account.component.html',
})
export class MyAccountComponent implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);
  private router = inject(Router);

  username = '';
  roleNames = '';
  isLoading = true;
  isSavingProfile = false;
  isSavingPassword = false;

  /** Which password boxes are currently revealed (the eye toggles). */
  show: Record<'current' | 'next' | 'confirm' | 'pin', boolean> = {
    current: false,
    next: false,
    confirm: false,
    pin: false,
  };

  profileForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    mobile: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
  });

  passwordForm: FormGroup = this.fb.group(
    {
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
      // Optional: a 4-digit POS PIN, changed under the same current-password
      // check because it is a credential too.
      pin: ['', Validators.pattern(/^\d{4}$/)],
    },
    { validators: MyAccountComponent.passwordsMatch },
  );

  /** Cross-field check so the confirm box must equal the new password. */
  private static passwordsMatch(
    group: AbstractControl,
  ): ValidationErrors | null {
    const next = group.get('newPassword')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return next && confirm && next !== confirm
      ? { passwordMismatch: true }
      : null;
  }

  ngOnInit(): void {
    this.authService.getLoggedInUser().subscribe({
      next: (user: any) => {
        this.username = user?.username || '';
        this.roleNames =
          (user?.roles || [])
            .map((r: any) => r?.role?.name)
            .filter(Boolean)
            .join(', ') || '—';
        this.profileForm.patchValue({
          name: user?.name || '',
          email: user?.email || '',
          mobile: user?.mobile || '',
        });
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toastr.error('Failed to load your account details');
      },
    });
  }

  toggle(field: 'current' | 'next' | 'confirm' | 'pin'): void {
    this.show[field] = !this.show[field];
  }

  saveProfile(): void {
    if (this.profileForm.invalid || this.isSavingProfile) {
      this.profileForm.markAllAsTouched();
      return;
    }
    this.isSavingProfile = true;
    this.authService.updateMyProfile(this.profileForm.value).subscribe({
      next: (user: any) => {
        this.isSavingProfile = false;
        this.authService.refreshStoredUser(user);
        this.toastr.success('Your details have been updated');
      },
      error: (err) => {
        this.isSavingProfile = false;
        this.toastr.error(err?.error?.message || 'Failed to update details');
      },
    });
  }

  savePassword(): void {
    if (this.passwordForm.invalid || this.isSavingPassword) {
      this.passwordForm.markAllAsTouched();
      if (this.passwordForm.errors?.['passwordMismatch'])
        this.toastr.error('New password and confirmation do not match');
      return;
    }
    this.isSavingPassword = true;
    const { currentPassword, newPassword, pin } = this.passwordForm.value;
    this.authService
      .updateMyProfile({
        currentPassword,
        newPassword,
        ...(pin ? { pin } : {}),
      })
      .subscribe({
        next: (user: any) => {
          this.isSavingPassword = false;
          // Refreshes mustChangePassword too, so the default-password banner
          // clears without a re-login.
          this.authService.refreshStoredUser(user);
          this.passwordForm.reset();
          this.toastr.success(
            'Password updated. Use it the next time you sign in.',
          );
        },
        error: (err) => {
          this.isSavingPassword = false;
          this.toastr.error(err?.error?.message || 'Failed to update password');
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/dashboard']);
  }
}
